<script setup lang="ts">
import { computed } from 'vue';

import type { ICountriesFeesItem, ICountryFeesSettingsForm, ICountryFeesSettingsFormSchema } from '@modules/admin-fees/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import { TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES } from '@modules/admin-fees/constants';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { assert, isNumber } from '@utils/guards';

import type { ICountryFeesSettingsDialogEmits, ICountryFeesSettingsDialogProps } from './types';

const props = defineProps<ICountryFeesSettingsDialogProps>();
const emit = defineEmits<ICountryFeesSettingsDialogEmits>();

const visible = defineModel<boolean>('visible');

const { getTranslation } = useI18n();

const currentCountryFees = computed<ICountriesFeesItem | undefined>(() => props.countryFeesFilters?.find((item) => props.selectedCountryFeesIndex === item.index));

const isAllCountries = computed(() => currentCountryFees.value?.isAllCountries);

const countriesOptions = computed<IFormSelectInputOption<number, 'id', 'value'>[]>(() => {
  if (!props.countries) {
    return [];
  }

  const selectedCountries = props.countryFeesFilters.filter((item) => props.selectedCountryFeesIndex !== item.index && !item._destroy).flatMap((item) => item.value);

  return props.countries
    .filter((item) => !selectedCountries.includes(item.country_code))
    .map((item) => ({
      id: item.country_code,
      value: item.name,
      check: currentCountryFees.value?.value.includes(item.country_code),
    }));
});

const formSchema = computed<ICountryFeesSettingsFormSchema>(() => ({
  value: {
    type: EFormInputTypes.MULTISELECT_BOX,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'SETTINGS_FORM_COUNTRIES').value,
    options: countriesOptions.value,
    optionLabel: 'value',
    optionValue: 'id',
    required: !isAllCountries.value,
    disabled: isAllCountries.value,
    showInfoMessage: true,
    infoMessage: isAllCountries.value
      ? getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'SETTINGS_FORM_READONLY_INFO').value
      : getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'SETTINGS_FORM_PLAYER_COUNTRY_INFO').value,
    messageSeverity: 'info',
    requiredErrorText: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'VALIDATIONS_COUNTRY_IS_REQUIRED').value,
    value: isAllCountries.value ? countriesOptions.value.map(({ id }) => id) : currentCountryFees.value?.value,
  },
  name: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'SETTINGS_FORM_NAME').value,
    value: isAllCountries.value ? currentCountryFees.value?.emptyLabel : currentCountryFees.value?.name,
    disabled: isAllCountries.value,
  },
  deposit_percent: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'SETTINGS_FORM_DEPOSIT').value,
    value: currentCountryFees.value?.deposit_percent,
    required: true,
    validation: {
      minValue: {
        message: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'VALIDATIONS_LESS_THAN_0').value,
        value: 0,
      },
      maxValue: {
        message: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'VALIDATIONS_GREATER_THAN_100').value,
        value: 100,
      },
    },
  },
  withdrawals_percent: {
    type: EFormInputTypes.NUMBER,
    label: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'SETTINGS_FORM_WITHDRAWALS').value,
    value: currentCountryFees.value?.withdrawals_percent,
    required: true,
    validation: {
      minValue: {
        message: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'VALIDATIONS_LESS_THAN_0').value,
        value: 0,
      },
      maxValue: {
        message: getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'VALIDATIONS_GREATER_THAN_100').value,
        value: 100,
      },
    },
  },
}));

function onSubmit(result: ICountryFeesSettingsForm): void {
  assert(isNumber(props.selectedCountryFeesIndex));
  emit('save', { index: props.selectedCountryFeesIndex, data: { ...result, value: isAllCountries.value ? [] : result.value } });
  visible.value = false;
}
</script>

<template>
  <VDialog
    :visible="visible"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'SETTINGS_HEADER')"
    @hide="visible = false"
  >
    <VForm
      :schema="formSchema"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'SETTINGS_FORM_SAVE')"
      :readonly="readonly"
      reverse-buttons
      @submit="onSubmit"
    >
      <template #secondaryBtn>
        <VButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_ADMIN_FEES, 'SETTINGS_FORM_CANCEL')"
          outlined
          @click="visible = false"
        />
      </template>
    </VForm>
  </VDialog>
</template>
